// Shopify cart through the Ajax Cart API: drawer, add-to-cart feedback and quantity changes.
// Every change asks Shopify to re-render the cart-drawer section (and the cart page's main section, if open),
// so prices, discounts, subscriptions and lines added by apps always match the real cart.
// Apps that change the cart themselves can call window.mpCart.refresh() or dispatch 'cart:refresh' on document;
// after every change the theme dispatches 'cart:updated' with the cart JSON in event.detail.
(() => {
  const MP = window.MP || {};
  const routes = MP.routes || {};
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const DRAWER = 'cart-drawer';
  const drawerHost = () => document.getElementById(`shopify-section-${DRAWER}`);
  const dialog = () => document.querySelector('#cart-preview');
  const trigger = document.querySelector('.cart-link');
  // The cart page's section re-renders together with the drawer.
  const pageSection = () => document.querySelector('[data-cart-page]')?.dataset.sectionId;
  const sectionIds = () => [DRAWER, pageSection()].filter(Boolean);

  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const render = sections => {
    if (!sections) return;
    const drawer = sections[DRAWER] && parse(sections[DRAWER]).querySelector('#cart-preview');
    if (drawer) {
      dialog().querySelector('.cart-shell').replaceWith(drawer.querySelector('.cart-shell'));
      dialog().dataset.cartCount = drawer.dataset.cartCount;
      setBadge(Number(drawer.dataset.cartCount));
    }
    const id = pageSection();
    if (id && sections[id]) {
      const next = parse(sections[id]).querySelector('[data-cart-page]');
      if (next) document.querySelector('[data-cart-page]').replaceWith(next);
    }
  };
  const setBadge = count => {
    if (!trigger) return;
    const badge = trigger.querySelector('.cart-badge');
    badge.hidden = count === 0;
    badge.textContent = count > 99 ? '99+' : String(count);
    trigger.setAttribute('aria-label', trigger.getAttribute('aria-label').replace(/\d+/, String(count)));
  };

  const post = (url, body) => fetch(`${url}.js`, {
    method: 'POST',
    headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body instanceof FormData ? body : JSON.stringify(body)
  }).then(async response => {
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || MP.strings?.error || 'Error');
    return data;
  });
  const announce = () => fetch(`${routes.cart}.js`).then(r => r.json()).then(cart => {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  }).catch(() => {});

  // Adds a FormData (product form, so app fields like properties[...] and selling_plan travel along) or {items:[...]}.
  const add = payload => {
    const ids = sectionIds();
    let body = payload;
    if (payload instanceof FormData) {
      payload.set('sections', ids.join(','));
      payload.set('sections_url', location.pathname);
    } else body = { ...payload, sections: ids.join(','), sections_url: location.pathname };
    return post(routes.cartAdd, body).then(data => { render(data.sections); announce(); return data; });
  };
  const change = (line, quantity) => post(routes.cartChange, { line, quantity, sections: sectionIds().join(','), sections_url: location.pathname })
    .then(data => { render(data.sections); announce(); return data; });
  const refresh = () => fetch(`${location.pathname}?sections=${sectionIds().join(',')}`).then(r => r.json()).then(render).then(announce);

  // Drawer open / close.
  const open = () => {
    const d = dialog();
    if (!d.open) d.showModal();
    document.body.classList.add('cart-open');
    requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('is-open')));
    d.querySelector('.cart-close')?.focus();
  };
  const close = () => {
    const d = dialog();
    d.classList.remove('is-open');
    document.body.classList.remove('cart-open');
    const finish = () => { if (d.open && !d.classList.contains('is-open')) d.close(); trigger?.focus({ preventScroll: true }); };
    if (reduce.matches) return finish();
    d.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 500);
  };

  // Feedback: football into the goal inside the button, badge pop, toast.
  const bump = () => {
    if (reduce.matches || !trigger) return;
    trigger.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.16) rotate(-8deg)' }, { transform: 'scale(1)' }], { duration: 420, easing: 'cubic-bezier(.23,1,.32,1)' });
    trigger.querySelector('.cart-badge').animate([{ transform: 'scale(.4)' }, { transform: 'scale(1.3)', offset: 0.55 }, { transform: 'scale(1)' }], { duration: 420, easing: 'cubic-bezier(.23,1,.32,1)' });
  };
  let toast, toastTimer;
  const showToast = ({ name, meta, image, error }) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML = `<img alt=""><div class="cart-toast-copy"><span class="cart-toast-kicker"><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9"/><path d="m6 10.3 2.7 2.6L14 7.6" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span></span></span><strong></strong><span class="cart-toast-meta"></span></div><button class="cart-toast-close" type="button" aria-label="×">×</button><div class="cart-toast-actions"><a class="cart-toast-view" href="${routes.cart}">Warenkorb ansehen</a><a class="cart-toast-checkout" href="${MP.root || '/'}checkout">Zur Kasse <svg class="i-ext" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 11.5 11.5 4.5M6 4.5h5.5V10"/></svg></a></div>`;
      document.body.append(toast);
      const hide = () => toast.classList.remove('is-visible');
      toast.querySelector('.cart-toast-close').addEventListener('click', hide);
      toast.addEventListener('pointerenter', () => clearTimeout(toastTimer));
      toast.addEventListener('pointerleave', () => { toastTimer = setTimeout(hide, 2500); });
    }
    toast.classList.toggle('is-error', Boolean(error));
    toast.querySelector('img').src = image || '';
    toast.querySelector('img').hidden = !image;
    toast.querySelector('.cart-toast-kicker span').textContent = error ? '!' : (MP.strings?.added || 'Im Warenkorb');
    toast.querySelector('strong').textContent = error || name || '';
    toast.querySelector('.cart-toast-meta').textContent = error ? '' : meta || '';
    toast.querySelector('.cart-toast-actions').hidden = Boolean(error);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4200);
  };
  const kickMarkup = '<span class="kick" aria-hidden="true"><svg class="kick-goal" viewBox="0 0 36 28"><g class="kick-net"><path class="kick-fill" d="M4 4h28v22H4z"/><path class="kick-mesh" d="M4 9.5h28M4 15h28M4 20.5h28M10 4v22M16 4v22M22 4v22M28 4v22"/></g><path class="kick-frame" d="M3 27V3h30v24"/></svg><svg class="kick-ball" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m12 8.2 3.6 2.6-1.4 4.2H9.8l-1.4-4.2z"/><path d="M12 8.2V3M15.6 10.8l4.8-1.6M14.2 15l2.9 4.2M9.8 15l-2.9 4.2M8.4 10.8 3.6 9.2"/></svg></span>';
  const kick = button => {
    if (reduce.matches || !button || !button.animate) return Promise.resolve();
    button.querySelector('.kick')?.remove();
    button.insertAdjacentHTML('beforeend', kickMarkup);
    const el = button.querySelector('.kick');
    const ball = el.querySelector('.kick-ball');
    const goal = el.querySelector('.kick-goal');
    const net = el.querySelector('.kick-net');
    const b = button.getBoundingClientRect();
    const icon = button.querySelector(':scope > svg');
    if (icon) { const i = icon.getBoundingClientRect(); ball.style.height = `${i.height}px`; ball.style.left = `${i.left - b.left}px`; }
    button.classList.add('is-kicking');
    const g = goal.getBoundingClientRect(), o = ball.getBoundingClientRect();
    const distance = g.left + g.width * 0.55 - o.left - o.width / 2;
    goal.animate([{ opacity: 0, transform: 'translateY(-50%) scale(.6)' }, { opacity: 1, transform: 'translateY(-50%) scale(1)' }], { duration: 220, easing: 'cubic-bezier(.23,1,.32,1)', fill: 'both' });
    const shot = ball.animate([
      { transform: 'translateY(-50%) translateX(0) rotate(-120deg) scale(.3)', opacity: 0, offset: 0 },
      { transform: 'translateY(-50%) translateX(0) rotate(0deg) scale(1.15)', opacity: 1, offset: 0.18 },
      { transform: 'translateY(-50%) translateX(-3px) rotate(-20deg) scale(1)', opacity: 1, offset: 0.28 },
      { transform: `translateY(calc(-50% - ${b.height * 0.16}px)) translateX(${distance * 0.55}px) rotate(300deg)`, opacity: 1, offset: 0.6 },
      { transform: `translateY(-50%) translateX(${distance}px) rotate(560deg) scale(.86)`, opacity: 1 }
    ], { duration: 760, easing: 'cubic-bezier(.33,0,.2,1)', fill: 'forwards' });
    return shot.finished.then(() => {
      net.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(1.28)', offset: 0.35 }, { transform: 'scaleX(.96)', offset: 0.7 }, { transform: 'scaleX(1)' }], { duration: 380, easing: 'ease-out' });
      return new Promise(done => setTimeout(done, 320));
    }).catch(() => {}).then(() => {
      const out = el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      return out.finished.catch(() => {}).then(() => { el.remove(); button.classList.remove('is-kicking'); });
    });
  };

  // Shared entry point for product forms, cards, the restock card and the drawer recommendation.
  // payload: FormData or { items: [{ id, quantity, properties }] }; name/meta/image feed the toast.
  const addWithFeedback = ({ payload, button, name, meta, image }) => {
    const request = add(payload);
    return Promise.all([request, kick(button)]).then(([data]) => {
      bump();
      const line = data.items?.[0] || data;
      if (MP.cartType === 'page') { location.href = routes.cart; return data; }
      if (MP.cartType === 'drawer' && !document.querySelector('[data-cart-page]')) open();
      else showToast({ name: name || line.product_title, meta: meta || (line.variant_title || ''), image: image || line.image });
      return data;
    }).catch(error => {
      button?.querySelector('.kick')?.remove();
      button?.classList.remove('is-kicking');
      showToast({ error: error.message });
      throw error;
    });
  };

  window.mpCart = { add, change, refresh, open, close, addWithFeedback };

  // Header icon opens the drawer (on the cart page itself it just scrolls up); without JS it is a plain link.
  trigger?.addEventListener('click', event => {
    if (MP.cartType === 'page') return;
    event.preventDefault();
    if (document.querySelector('[data-cart-page]')) return scrollTo({ top: 0, behavior: 'smooth' });
    open();
  });
  document.addEventListener('click', event => {
    const d = dialog();
    if (!d) return;
    if (d.open && (event.target === d || event.target.closest('#cart-preview .cart-close, #cart-preview .cart-empty-link'))) {
      close();
      return;
    }
    const button = event.target.closest('[data-cart-page] button[data-action], #cart-preview button[data-action]');
    if (button) {
      const row = button.closest('[data-line]');
      const qty = Number(row.dataset.quantity);
      const next = button.dataset.action === 'remove' ? 0 : qty + (button.dataset.action === 'increase' ? 1 : -1);
      row.classList.add('is-updating');
      change(Number(row.dataset.line), Math.max(0, next)).catch(error => { row.classList.remove('is-updating'); showToast({ error: error.message }); });
      return;
    }
    const removeLink = event.target.closest('[data-cart-page] [data-action-link="remove"]');
    if (removeLink) {
      event.preventDefault();
      const row = removeLink.closest('[data-line]');
      row.classList.add('is-updating');
      change(Number(row.dataset.line), 0).catch(error => { row.classList.remove('is-updating'); showToast({ error: error.message }); });
      return;
    }
    const quick = event.target.closest('#cart-preview [data-add-variant]');
    if (quick) addWithFeedback({ payload: { items: [{ id: Number(quick.dataset.addVariant), quantity: 1 }] }, button: quick });
  });
  document.addEventListener('cancel', event => { if (event.target === dialog()) { event.preventDefault(); close(); } }, true);
  document.addEventListener('cart:refresh', refresh);
  // Cart page: typed quantities and the order note save without reloading.
  let noteTimer;
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-page] .cp-qty-input');
    if (input) {
      const row = input.closest('[data-line]');
      row.classList.add('is-updating');
      change(Number(row.dataset.line), Math.max(0, Number(input.value) || 0)).catch(error => { row.classList.remove('is-updating'); showToast({ error: error.message }); });
    }
  });
  document.addEventListener('input', event => {
    const note = event.target.closest('[data-cart-page] textarea[name="note"]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => post(routes.cartUpdate, { note: note.value }).catch(() => {}), 500);
  });
})();
